import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Link,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    Skeleton,
    Tooltip,
    Typography,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    FormControl,
    InputLabel,
} from "@mui/material"
import {
    AttachFile,
    Close,
    DeleteOutlined,
    FileUpload,
    MoreVert,
    PictureAsPdf,
    Send,
    Image as ImageIcon,
    InsertDriveFile,
    NotificationsActiveOutlined,
    SaveAlt,
    TextsmsOutlined,
} from "@mui/icons-material"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import SimpleText from "../../../components/Shared/SimpleText"
import { api } from "../../../services/Main/Api"
import { useParams } from "react-router-dom"
import moment from "moment"
import SimpleDialog from "../../../components/Shared/SimpleDialog"
import PaginationGlobal from "../../../components/Shared/PaginationGlobal"
import { useSnackbar } from "notistack"
import Context from "../../../contexts/Context"
import AnimationGlobal from "../../../components/Shared/animations/AnimationGlobal"
import notFound from "../../../assets/animations/emptyBox.json"

const RentNotes = (props) => {
    const { isLoading } = props
    const { filters, setFilters } = useContext(Context)
    const params = useParams()
    const { id } = params
    const { enqueueSnackbar } = useSnackbar()

    const [data, setData] = useState([])
    const [reqData, setReqData] = useState(null)
    const [reqDataUpdate, setReqDataUpdate] = useState(null)

    const [openMenu, setOpenMenu] = useState(false)
    const [rentNoteId, setRentNoteId] = useState(null)
    const [anchor, setAnchor] = useState(null)

    const [openDelete, setOpenDelete] = useState(false)

    // Dialog criar nota
    const [openCreateDialog, setOpenCreateDialog] = useState(false)

    // Choices (categorias reais do contrato)
    const [categoryChoices, setCategoryChoices] = useState([])
    const [isLoadingChoices, setIsLoadingChoices] = useState(false)

    // Arquivos para upload na criação de nota
    const [createFiles, setCreateFiles] = useState([])
    const [isUploading, setIsUploading] = useState(false)
    const createFileInputRef = useRef(null)

    // Upload de arquivos em nota existente
    const [uploadingNoteId, setUploadingNoteId] = useState(null)

    // Delete de documento individual
    const [openDeleteDoc, setOpenDeleteDoc] = useState(false)
    const [deleteDocId, setDeleteDocId] = useState(null)

    const sizeMatch = useMediaQuery("@media (min-width:700px)")
    const lastChoicesContractIdRef = useRef(null)

    ///////////////////////////
    //// Helpers //////////////
    ///////////////////////////

    const IMG_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif"]

    function isImageFile(url) {
        if (!url) return false
        const lower = url.toLowerCase()
        return IMG_EXTENSIONS.some((ext) => lower.includes(ext))
    }

    function isPdfFile(url) {
        if (!url) return false
        return url.toLowerCase().includes(".pdf")
    }

    function getFileName(url) {
        if (!url) return "arquivo"
        return url.substring(url.lastIndexOf("/") + 1)
    }

    function normalizeUser(note) {
        if (!note) return { name: "BlocoB3", avatar: null }
        const u = note?.user

        if (!u) return { name: "BlocoB3", avatar: null }
        if (typeof u === "string") return { name: u, avatar: note?.user_avatar || null }

        return {
            name: u?.name || u?.full_name || u?.first_name || u?.email || "BlocoB3",
            avatar: u?.avatar || u?.photo || u?.image || note?.user_avatar || null,
        }
    }

    function isButtonDisabled(reqDataUpdate, note) {
        const isMessageEmpty = !reqDataUpdate?.message || reqDataUpdate?.message?.length < 1
        const isMessageUnchanged = reqDataUpdate?.message === note.message
        return isMessageEmpty || isMessageUnchanged
    }

    function resolveCategoryLabel(categoryIdOrValue) {
        // Se tua API de nota retorna category como ID (number), resolvemos pelo choices
        if (categoryIdOrValue === null || categoryIdOrValue === undefined || categoryIdOrValue === "") return null

        if (typeof categoryIdOrValue === "number") {
            const found = categoryChoices?.find((c) => c?.id === categoryIdOrValue)
            return found?.value || `Categoria #${categoryIdOrValue}`
        }

        // se já vier string (ex: "Aluguel")
        if (typeof categoryIdOrValue === "string") return categoryIdOrValue

        // se vier objeto (defensivo)
        if (typeof categoryIdOrValue === "object") {
            return categoryIdOrValue?.value || categoryIdOrValue?.name || categoryIdOrValue?.label || "Categoria"
        }

        return null
    }

    /////////////////////////////
    //////// Data ///////////////
    /////////////////////////////

    const formattedNotes = useMemo(() => {
        return data?.results?.map((note) => {
            const match = note?.message?.match?.(/Valor do contrato de locação foi alterado para R\$(\d+(\.\d+)?)/)
            if (match) {
                const value = parseFloat(match[1])
                const formattedValue = value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                return {
                    ...note,
                    formattedMessage: note.message.replace(
                        match[0],
                        `Valor do contrato de locação foi alterado para ${formattedValue}`
                    ),
                }
            }
            return note
        })
    }, [data])

    /////////////////////////////
    //////// Requisitions ///////
    /////////////////////////////

    function getRentContractChoices(force = false) {
        if (!id) return
        if (!force && lastChoicesContractIdRef.current === id && categoryChoices?.length) return

        setIsLoadingChoices(true)
        api
            .get(`/rent-contract/choices/`, { params: { rent_contract: id } })
            .then((response) => {
                const choices = response?.data?.category_choices || []
                setCategoryChoices(choices)
                lastChoicesContractIdRef.current = id

                if (openCreateDialog && !reqData?.category && choices?.length) {
                    setReqData((prev) => ({ ...(prev || {}), category: choices[0]?.id }))
                }
            })
            .catch((error) => {
                console.log(error)
                enqueueSnackbar("Erro ao carregar categorias do contrato", { variant: "error" })
                setCategoryChoices([])
            })
            .finally(() => setIsLoadingChoices(false))
    }

    function getData() {
        const formatFilters = {
            rent_contract: id,
            // ✅ novo filtro por categoria (ajuste o nome do param se teu backend usar outro)
            category: filters?.note_category || "",
        }

        api
            .get(`rent_contract/internal_note_rent/`, { params: formatFilters })
            .then((response) => {
                setData(response.data)
            })
            .catch((error) => {
                console.log(error)
            })
    }

    function uploadDocuments(noteId, files) {
        if (!files?.length) return Promise.resolve()
        const formData = new FormData()
        formData.append("internal_note_rent", noteId)
        files.forEach((file) => {
            formData.append("file", file)
        })
        return api.post("rent_contract/internal_note_rent/document/", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        })
    }

    function deleteDocument(docId) {
        api
            .delete(`rent_contract/internal_note_rent/document/${docId}/`)
            .then(() => {
                getData()
                setOpenDeleteDoc(false)
                setDeleteDocId(null)
                enqueueSnackbar("Documento removido", { variant: "success" })
            })
            .catch((error) => {
                console.log(error)
                enqueueSnackbar("Erro ao remover documento", { variant: "error" })
            })
    }

    function handleUploadToExistingNote(noteId, files) {
        if (!files?.length) return
        const numericId = Number(noteId)
        setUploadingNoteId(numericId)
        uploadDocuments(numericId, Array.from(files))
            .then(() => {
                getData()
                enqueueSnackbar("Documento(s) anexado(s)", { variant: "success" })
            })
            .catch((error) => {
                console.log(error)
                enqueueSnackbar("Erro ao anexar documento(s)", { variant: "error" })
            })
            .finally(() => setUploadingNoteId(null))
    }

    function postRentNote() {
        const payload = {
            ...(reqData || {}),
            rent_contract: id,
            category: reqData?.category || null,
            notify_at: reqData?.notify_at || null,
        }

        setIsUploading(true)
        api
            .post(`rent_contract/internal_note_rent/`, payload)
            .then((response) => {
                const noteId = response?.data?.id
                if (createFiles.length && noteId) {
                    return uploadDocuments(noteId, createFiles).then(() => {
                        getData()
                        enqueueSnackbar("Anotação registrada com anexo(s)", { variant: "success" })
                    })
                } else {
                    getData()
                    enqueueSnackbar("Anotação registrada", { variant: "success" })
                }
            })
            .catch((error) => {
                enqueueSnackbar("Erro ao registrar anotação", { variant: "error" })
                console.log(error)
            })
            .finally(() => {
                setReqData(null)
                setCreateFiles([])
                setOpenCreateDialog(false)
                setIsUploading(false)
            })
    }

    function updateRentNote(noteId) {
        const payload = {
            ...reqDataUpdate,
            rent_contract: id,
        }

        api
            .put(`rent_contract/internal_note_rent/${noteId}/`, payload)
            .then(() => {
                getData()
                setRentNoteId(null)
                setReqDataUpdate(null)
                enqueueSnackbar("Anotação atualizada", { variant: "success" })
            })
            .catch((error) => {
                enqueueSnackbar("Erro ao atualizar anotação", { variant: "error" })
                console.log(error)
            })
    }

    function deleteRentNote(noteId) {
        api
            .delete(`rent_contract/internal_note_rent/${noteId}/`)
            .then(() => {
                getData()
                setOpenDelete(false)
                enqueueSnackbar("Anotação deletada", { variant: "success" })
            })
            .catch((error) => {
                console.log(error)
                enqueueSnackbar("Erro ao deletar anotação", { variant: "error" })
            })
    }

    //////////////////////////////
    //////// Effects /////////////
    //////////////////////////////

    useEffect(() => {
        setFilters((prevState) => ({
            ...prevState,
            rent_contract: id,
            note_category: "", // ✅ filtro novo
        }))

    }, [])

    useEffect(() => {
        if (filters) getData()

    }, [filters])

    useEffect(() => {
        if (id) getRentContractChoices(true)

    }, [id])

    useEffect(() => {
        if (openCreateDialog) getRentContractChoices(false)

    }, [openCreateDialog])

    const canSendCreate = !!reqData?.message

    //////////////////////////////
    //////// UI: Filter Category /
    //////////////////////////////

    function CategoryFilter() {
        const selected = filters?.note_category || ""

        return (
            <FormControl size="small" sx={{ minWidth: 220 }} disabled={isLoadingChoices}>
                <InputLabel>Categoria</InputLabel>
                <Select
                    value={selected}
                    size="small"
                    label="Categoria"
                    onChange={(e) => {
                        const value = e.target.value
                        setFilters((prev) => ({
                            ...prev,
                            note_category: value,
                            page: 1, // evita página “vazia” ao filtrar
                        }))
                    }}

                    // startAdornment={
                    //     <InputAdornment position="start" sx={{ mr: "0.4rem" }}>
                    //         <FilterList sx={{ color: "primary.main" }} />
                    //     </InputAdornment>
                    // }
                >
                    <MenuItem value="">Todas</MenuItem>
                    {categoryChoices?.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                            {c.value}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        )
    }

    return (
        <Grid item xs={12}>
            {!isLoading ? (
                <>
                    <CardHeader
                        avatar={
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                                <TextsmsOutlined />
                            </Avatar>
                        }
                        title={"Notas Internas"}
                        action={
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: sizeMatch ? "row" : "column",
                                    alignItems: sizeMatch ? "center" : "stretch",
                                    gap: sizeMatch ? 1 : 0.8,
                                }}
                            >
                                {/* ✅ Filtro por categoria ao lado do botão */}
                                <CategoryFilter />

                                <Button
                                    onClick={() => {
                                        setOpenCreateDialog(true)
                                        setReqData({
                                            message: "",
                                            category: "",
                                            notify_at: "",
                                        })
                                    }}
                                    variant="contained"
                                    size="small"
                                    startIcon={<TextsmsOutlined />}
                                >
                                    Criar nota
                                </Button>
                            </Box>
                        }
                    />

                    <Divider sx={{ mb: ".3rem" }} textAlign={"center"} />

                    <CardContent>
                        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                            {data?.results?.length ? (
                                <>
                                    {formattedNotes?.map((note, index) => {
                                        const user = normalizeUser(note)
                                        const categoryLabel = resolveCategoryLabel(note?.category)

                                        return (
                                            <Card key={index} sx={{ marginBottom: "2rem" }}>
                                                <CardHeader
                                                    avatar={
                                                        <Avatar
                                                            src={note?.avatar || undefined}
                                                            sx={{ bgcolor: "primary.main" }}
                                                            imgProps={{ referrerPolicy: "no-referrer" }}
                                                        >
                                                            {(user?.name || "B")[0]?.toUpperCase?.() || "B"}
                                                        </Avatar>
                                                    }
                                                    title={
                                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 1,
                                                                    flexWrap: "wrap",
                                                                }}
                                                            >
                                                                <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
                                                                    {user?.name || "BlocoB3"}
                                                                </Typography>
                                                            </Box>

                                                            <Typography variant="caption" color="text.secondary">
                                                                {moment(note?.created_at).locale("pt-br").format("DD/MM/YYYY [às] HH:mm")}
                                                                {note?.notify_at ? (
                                                                    <>
                                                                        {" "}
                                                                        • <NotificationsActiveOutlined sx={{ fontSize: 14, mb: "-2px" }} /> Notificar em{" "}
                                                                        {moment(note?.notify_at).locale("pt-br").format("DD/MM/YYYY")}
                                                                    </>
                                                                ) : null}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    action={
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            {/* ✅ Chip com category */}
                                                            {categoryLabel && (
                                                                <Chip
                                                                    size="small"
                                                                    label={categoryLabel}
                                                                    color="default"
                                                                    variant="filled"
                                                                />
                                                            )}

                                                            {note?.user && (
                                                                <Tooltip
                                                                    title={rentNoteId ? "Salve ou Cancele a edição" : "Editar ou Excluir"}
                                                                    arrow
                                                                    placement={"top"}
                                                                >
                                                                    <IconButton
                                                                        onClick={(e) => {
                                                                            setOpenMenu(true)
                                                                            setRentNoteId({ menu: note.id })
                                                                            setAnchor(e.currentTarget)
                                                                            e.stopPropagation()
                                                                        }}
                                                                    >
                                                                        <MoreVert />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}

                                                            {note?.user && (
                                                                <Menu
                                                                    open={!!openMenu}
                                                                    anchorEl={anchor || null}
                                                                    onClose={(e) => {
                                                                        e.stopPropagation()
                                                                        setOpenMenu(false)
                                                                    }}
                                                                    PaperProps={{ sx: { width: 220, maxWidth: "100%" } }}
                                                                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                                                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                                                >
                                                                    {/*<MenuItem*/}
                                                                    {/*    onClick={(e) => {*/}
                                                                    {/*        setRentNoteId({ editable: rentNoteId?.menu })*/}
                                                                    {/*        setReqDataUpdate({ message: note.message })*/}
                                                                    {/*        setOpenMenu(false)*/}
                                                                    {/*        e.stopPropagation()*/}
                                                                    {/*    }}*/}
                                                                    {/*    sx={{ color: "text.secondary" }}*/}
                                                                    {/*>*/}
                                                                    {/*    <ListItemIcon>*/}
                                                                    {/*        <EditOutlined />*/}
                                                                    {/*    </ListItemIcon>*/}
                                                                    {/*    <ListItemText*/}
                                                                    {/*        primary="Editar anotação"*/}
                                                                    {/*        primaryTypographyProps={{ variant: "body2" }}*/}
                                                                    {/*    />*/}
                                                                    {/*</MenuItem>*/}

                                                                    <MenuItem
                                                                        onClick={(e) => {
                                                                            setOpenDelete(true)
                                                                            setOpenMenu(false)
                                                                            e.stopPropagation()
                                                                        }}
                                                                        sx={{ color: "text.secondary" }}
                                                                    >
                                                                        <ListItemIcon>
                                                                            <DeleteOutlined />
                                                                        </ListItemIcon>
                                                                        <ListItemText
                                                                            primary="Excluir anotação"
                                                                            primaryTypographyProps={{ variant: "body2" }}
                                                                        />
                                                                    </MenuItem>
                                                                </Menu>
                                                            )}
                                                        </Box>
                                                    }
                                                />

                                                <CardContent sx={{ pt: 0 }}>
                                                    {note?.id !== rentNoteId?.editable ? (
                                                        <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.8" }}>
                                                            {note?.message}
                                                        </div>
                                                    ) : (
                                                        <Box>
                                                            <SimpleText
                                                                label={"Anotação"}
                                                                multiline
                                                                rows={5}
                                                                state={reqDataUpdate}
                                                                setState={setReqDataUpdate}
                                                                keyName={"message"}
                                                                directValue={reqDataUpdate?.message ?? note?.message}
                                                            />

                                                            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "0.5rem", gap: 1 }}>
                                                                <Button
                                                                    variant="text"
                                                                    onClick={() => {
                                                                        setRentNoteId(null)
                                                                        setReqDataUpdate(null)
                                                                    }}
                                                                >
                                                                    Cancelar
                                                                </Button>

                                                                <Button
                                                                    variant="text"
                                                                    endIcon={<SaveAlt />}
                                                                    disabled={isButtonDisabled(reqDataUpdate, note)}
                                                                    onClick={() => updateRentNote(rentNoteId?.editable)}
                                                                >
                                                                    Salvar
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* Documentos anexados */}
                                                    {note?.documents?.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                mt: 1.5,
                                                                display: "flex",
                                                                flexWrap: "wrap",
                                                                gap: 1.5,
                                                            }}
                                                        >
                                                            {note.documents.map((doc) => (
                                                                <Box
                                                                    key={doc.id}
                                                                    sx={{
                                                                        position: "relative",
                                                                        border: "1px solid",
                                                                        borderColor: "divider",
                                                                        borderRadius: 1,
                                                                        overflow: "hidden",
                                                                        width: 100,
                                                                        height: 100,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        bgcolor: "grey.50",
                                                                        "&:hover .doc-delete-btn": {
                                                                            opacity: 1,
                                                                        },
                                                                    }}
                                                                >
                                                                    <Link
                                                                        href={doc.file}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            width: "100%",
                                                                            height: "100%",
                                                                        }}
                                                                    >
                                                                        {isImageFile(doc.file) ? (
                                                                            <Box
                                                                                component="img"
                                                                                src={doc.file}
                                                                                alt={getFileName(doc.file)}
                                                                                sx={{
                                                                                    width: "100%",
                                                                                    height: "100%",
                                                                                    objectFit: "cover",
                                                                                }}
                                                                            />
                                                                        ) : isPdfFile(doc.file) ? (
                                                                            <Box sx={{ textAlign: "center", p: 0.5 }}>
                                                                                <PictureAsPdf sx={{ fontSize: 36, color: "error.main" }} />
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        display: "block",
                                                                                        overflow: "hidden",
                                                                                        textOverflow: "ellipsis",
                                                                                        whiteSpace: "nowrap",
                                                                                        maxWidth: 90,
                                                                                    }}
                                                                                >
                                                                                    {getFileName(doc.file)}
                                                                                </Typography>
                                                                            </Box>
                                                                        ) : (
                                                                            <Box sx={{ textAlign: "center", p: 0.5 }}>
                                                                                <InsertDriveFile sx={{ fontSize: 36, color: "text.secondary" }} />
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        display: "block",
                                                                                        overflow: "hidden",
                                                                                        textOverflow: "ellipsis",
                                                                                        whiteSpace: "nowrap",
                                                                                        maxWidth: 90,
                                                                                    }}
                                                                                >
                                                                                    {getFileName(doc.file)}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                    </Link>

                                                                    <IconButton
                                                                        className="doc-delete-btn"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setDeleteDocId(doc.id)
                                                                            setOpenDeleteDoc(true)
                                                                        }}
                                                                        sx={{
                                                                            position: "absolute",
                                                                            top: 2,
                                                                            right: 2,
                                                                            bgcolor: "rgba(255,255,255,0.85)",
                                                                            opacity: 0,
                                                                            transition: "opacity 0.2s",
                                                                            "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                                                                            p: 0.3,
                                                                        }}
                                                                    >
                                                                        <Close sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    )}

                                                    {/* Botão para anexar arquivo à nota existente */}
                                                    {note?.user && (
                                                        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                                            <input
                                                                type="file"
                                                                multiple
                                                                accept="image/*,.pdf"
                                                                style={{ display: "none" }}
                                                                ref={(el) => {
                                                                    if (el) el.dataset.noteId = note.id
                                                                }}
                                                                onChange={(e) => {
                                                                    const noteIdAttr = e.target.dataset.noteId
                                                                    handleUploadToExistingNote(noteIdAttr, e.target.files)
                                                                    e.target.value = ""
                                                                }}
                                                            />
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                startIcon={
                                                                    uploadingNoteId === note.id ? (
                                                                        <CircularProgress size={16} />
                                                                    ) : (
                                                                        <AttachFile sx={{ fontSize: 16 }} />
                                                                    )
                                                                }
                                                                disabled={uploadingNoteId === note.id}
                                                                onClick={(e) => {
                                                                    const input = e.currentTarget.parentElement.querySelector("input[type=file]")
                                                                    input?.click()
                                                                }}
                                                                sx={{ textTransform: "none", color: "text.secondary" }}
                                                            >
                                                                Anexar arquivo
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        )
                                    })}
                                </>
                            ) : (
                                <AnimationGlobal animation={notFound} text={"Nenhum resultado"} size={"200px"} fontSize={"24px"} />
                            )}
                        </Box>
                    </CardContent>

                    <PaginationGlobal state={data} filters={filters} setFilters={setFilters} />

                    {/* Dialog criar nota */}
                    <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} fullWidth maxWidth="md">
                        <DialogTitle
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar sx={{ bgcolor: "primary.main" }}>
                                    <TextsmsOutlined />
                                </Avatar>
                                <Box>
                                    <Typography sx={{ fontWeight: "bold" }}>Criar nota interna</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Contrato #{id}
                                    </Typography>
                                </Box>
                            </Box>

                            <IconButton onClick={() => setOpenCreateDialog(false)}>
                                <Close />
                            </IconButton>
                        </DialogTitle>

                        <DialogContent dividers>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {/* Categoria (populado do endpoint /rent-contract/choices/) */}
                                <FormControl size="small" fullWidth disabled={isLoadingChoices}>
                                    <InputLabel>Categoria</InputLabel>
                                    <Select
                                        value={reqData?.category ?? ""}
                                        label="Categoria"
                                        onChange={(e) =>
                                            setReqData((prev) => ({ ...(prev || {}), category: e.target.value }))
                                        }

                                    >
                                        <MenuItem value="">
                                            {isLoadingChoices ? "Carregando..." : "Sem categoria"}
                                        </MenuItem>

                                        {categoryChoices?.map((c) => (
                                            <MenuItem key={c.id} value={c.id}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                                                    <Typography>{c.value}</Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Mensagem */}
                                <SimpleText
                                    label={"Anotação"}
                                    multiline
                                    rows={6}
                                    state={reqData}
                                    setState={setReqData}
                                    keyName={"message"}
                                />

                                {/* Upload de arquivos */}
                                <Box>
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*,.pdf"
                                        ref={createFileInputRef}
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            setCreateFiles((prev) => [...prev, ...Array.from(e.target.files)])
                                            e.target.value = ""
                                        }}
                                    />
                                    <Box
                                        onClick={() => createFileInputRef.current?.click()}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderWidth: 2,
                                            borderStyle: "dashed",
                                            borderRadius: 2,
                                            borderColor: "secondary.light",
                                            bgcolor: "grey.50",
                                            py: 2,
                                            cursor: "pointer",
                                            "&:hover": { borderColor: "primary.main", bgcolor: "grey.100" },
                                        }}
                                    >
                                        <FileUpload sx={{ fontSize: 36, color: "green", opacity: 0.6 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Clique para selecionar arquivos (PDF, imagens)
                                        </Typography>
                                    </Box>

                                    {createFiles.length > 0 && (
                                        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            {createFiles.map((file, idx) => {
                                                const isImg = file.type?.startsWith("image/")
                                                return (
                                                    <Chip
                                                        key={idx}
                                                        icon={isImg ? <ImageIcon /> : <PictureAsPdf />}
                                                        label={file.name}
                                                        onDelete={() => setCreateFiles((prev) => prev.filter((_, i) => i !== idx))}
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                )
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </DialogContent>

                        <DialogActions sx={{ px: 2, display: "flex", justifyContent: "space-between" }}>
                            <Button
                                variant="text"
                                onClick={() => {
                                    setOpenCreateDialog(false)
                                    setReqData(null)
                                    setCreateFiles([])
                                }}
                            >
                                Cancelar
                            </Button>

                            <Button
                                variant="contained"
                                endIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <Send />}
                                disabled={!canSendCreate || isUploading}
                                onClick={postRentNote}
                                sx={{ mb: "0.25rem" }}
                            >
                                {isUploading ? "Enviando..." : "Enviar"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            ) : (
                <Skeleton height={300} width={"100%"} variant="rounded" />
            )}

            <SimpleDialog
                open={openDelete}
                setOpen={setOpenDelete}
                title={"Excluir anotação"}
                question={"Realmente quer excluir essa anotação?"}
                onClickConfirm={() => deleteRentNote(rentNoteId?.menu)}
            />

            <SimpleDialog
                open={openDeleteDoc}
                setOpen={setOpenDeleteDoc}
                title={"Excluir documento"}
                question={"Realmente quer excluir esse documento?"}
                onClickConfirm={() => deleteDocument(deleteDocId)}
            />
        </Grid>
    )
}

export default RentNotes
